/**
 * LLM Settings Tab Component
 *
 * Tab component for managing LLM provider configurations
 * within the System Settings page. Migrated from LLMSettingsPage.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Text,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  Settings20Regular,
  Copy20Regular,
  Play20Regular,
  Delete20Regular,
  ArrowClockwise20Regular,
  Add20Regular,
} from '@fluentui/react-icons';
import { LLMProviderModal } from '../LLMSettings/LLMProviderModal';
import { llmSettingsApi } from '../../services/llmSettingsApi';
import { Alert } from '../common/Alert';
import { useLLMProviders } from '../../hooks/useLLMProviders';
import { SavedProvider, LLMProviderConfig, LLMProviderRequest, DEFAULT_LLM_CONFIG } from '../../types/llmSettings';
import {
  DataGridCard,
  DataGridToolbar,
  ToolbarButton,
  ToolbarSearch,
  createTextColumn,
  createStatusColumn,
  createColumnWithRowActions,
  type ActionConfig,
} from '../layouts';

export const LLMSettingsTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Use React Query hook for data fetching and mutations
  const {
    providers: providersData,
    isLoading,
    isFetching,
    error: fetchError,
    saveProvider: saveProviderMutation,
    updateProvider: updateProviderMutation,
    deleteProvider: deleteProviderMutation,
    activateProvider: activateProviderMutation,
    refetch,
  } = useLLMProviders();

  // Memoize providers to avoid dependency array warnings
  const providers: SavedProvider[] = useMemo(
    () => providersData ?? [],
    [providersData]
  );

  // Local UI state for error messages (from mutations)
  const [localError, setLocalError] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingProvider, setEditingProvider] = useState<SavedProvider | null>(null);
  const [editingConfig, setEditingConfig] = useState<LLMProviderConfig | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProviderId, setDeletingProviderId] = useState<number | null>(null);

  // Bulk action state management
  const [selectedProviders, setSelectedProviders] = useState<Set<number>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState<boolean>(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string>('');

  // Derive error message from React Query error or local error
  const error = fetchError?.message || localError;

  const handleRetry = () => {
    setLocalError(null);
    refetch();
  };

  const handleAddProvider = () => {
    setModalMode('add');
    setEditingProvider(null);
    setEditingConfig(null);
    setIsDuplicating(false);
    setModalOpen(true);
  };

  const handleConfigureProvider = useCallback(async (providerId: number) => {
    const provider = providers.find((p) => p.id === providerId);
    if (provider) {
      try {
        const providerDetail = await llmSettingsApi.getProvider(providerId);
        setEditingProvider(provider);
        setEditingConfig(providerDetail.config);
        setModalMode('edit');
        setIsDuplicating(false);
        setModalOpen(true);
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : 'Failed to load provider configuration');
      }
    }
  }, [providers]);

  const handleSaveProvider = async (config: LLMProviderConfig) => {
    try {
      if (modalMode === 'edit' && editingProvider) {
        await updateProviderMutation({
          providerId: editingProvider.id,
          config,
          makeActive: false,
        });
      } else {
        const request: LLMProviderRequest = {
          config,
          makeActive: false,
        };
        await saveProviderMutation(request);
      }

      setModalOpen(false);
      // React Query will automatically refetch via onSuccess invalidation
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to save provider');
      throw err;
    }
  };

  const handleActivateProvider = useCallback(async (providerId: number) => {
    try {
      await activateProviderMutation(providerId);
      // React Query will automatically refetch via onSuccess invalidation
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to activate provider');
    }
  }, [activateProviderMutation]);

  const handleDeleteProvider = useCallback((providerId: number) => {
    setDeletingProviderId(providerId);
    setShowDeleteConfirm(true);
  }, []);

  const handleDuplicateProvider = useCallback(async (providerId: number) => {
    const provider = providers.find((p) => p.id === providerId);
    if (provider) {
      try {
        const providerDetail = await llmSettingsApi.getProvider(providerId);
        setEditingProvider(null);
        setEditingConfig(providerDetail.config);
        setModalMode('add');
        setIsDuplicating(true);
        setModalOpen(true);
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : 'Failed to load provider configuration for duplication');
      }
    }
  }, [providers]);

  const confirmDeleteProvider = async () => {
    if (deletingProviderId === null) return;

    try {
      await deleteProviderMutation(deletingProviderId);
      setShowDeleteConfirm(false);
      setDeletingProviderId(null);
      // React Query will automatically refetch via onSuccess invalidation
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to delete provider');
      setShowDeleteConfirm(false);
      setDeletingProviderId(null);
    }
  };

  // Filter providers based on search query
  const filteredProviders = useMemo(() => {
    if (!searchQuery.trim()) {
      return providers;
    }

    const query = searchQuery.toLowerCase();
    return providers.filter(
      (provider) =>
        provider.displayName.toLowerCase().includes(query) || provider.provider.toLowerCase().includes(query)
    );
  }, [providers, searchQuery]);

  // Handle selection changes from DataGridCard
  const handleSelectionChange = (ids: Set<string | number>) => {
    // Convert to Set<number> for internal state
    const numberIds = new Set<number>();
    ids.forEach((id) => numberIds.add(typeof id === 'number' ? id : parseInt(String(id), 10)));
    setSelectedProviders(numberIds);
  };

  // Handle bulk delete initiation
  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  // Define columns using factory functions
  const columns = useMemo(() => [
    createColumnWithRowActions<SavedProvider>({
      columnId: 'displayName',
      header: 'Provider Name',
      compare: (a, b) => a.displayName.localeCompare(b.displayName),
      renderContent: (provider) => (
        <span title={provider.displayName} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {provider.displayName}
        </span>
      ),
      getActions: (provider) => {
        const actions: ActionConfig[] = [
          {
            label: 'Configure',
            icon: <Settings20Regular />,
            onClick: () => handleConfigureProvider(provider.id),
          },
          {
            label: 'Duplicate',
            icon: <Copy20Regular />,
            onClick: () => handleDuplicateProvider(provider.id),
          },
        ];
        if (!provider.isActive) {
          actions.push({
            label: 'Activate',
            icon: <Play20Regular />,
            onClick: () => handleActivateProvider(provider.id),
          });
          actions.push({
            label: 'Delete',
            icon: <Delete20Regular />,
            onClick: () => handleDeleteProvider(provider.id),
          });
        }
        return actions;
      },
      getAriaLabel: (provider) => `Actions for ${provider.displayName}`,
    }),
    createTextColumn<SavedProvider>({
      columnId: 'provider',
      header: 'Type',
      getValue: (provider) => provider.provider.replace('_', ' '),
    }),
    createStatusColumn<SavedProvider>({
      columnId: 'status',
      header: 'Status',
      getStatus: (provider) => ({
        label: provider.isActive ? 'Active' : 'Inactive',
        type: provider.isActive ? 'success' : 'danger',
      }),
    }),
    createTextColumn<SavedProvider>({
      columnId: 'timeout',
      header: 'Timeout',
      getValue: (provider) => `${provider.timeoutSeconds ?? DEFAULT_LLM_CONFIG.timeoutSeconds}s`,
    }),
  ], [handleActivateProvider, handleConfigureProvider, handleDeleteProvider, handleDuplicateProvider]);

  // Handle bulk delete confirmation
  const handleConfirmBulkDelete = async () => {
    const selectedProviderIds = Array.from(selectedProviders);
    const deletableProviderIds = selectedProviderIds.filter((id) => {
      const provider = providers.find((p) => p.id === id);
      return provider && !provider.isActive;
    });

    const errors: string[] = [];

    try {
      for (const providerId of deletableProviderIds) {
        try {
          await deleteProviderMutation(providerId);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`${providerId}: ${errorMessage}`);
        }
      }

      // React Query will automatically refetch via onSuccess invalidation

      if (errors.length > 0) {
        if (errors.length === deletableProviderIds.length) {
          setBulkDeleteError(errors[0].split(': ')[1] || 'Network unavailable');
        } else {
          setBulkDeleteError('Failed to delete some providers');
        }
        setTimeout(() => setBulkDeleteError(''), 5000);

        const failedIds = errors.map((error) => parseInt(error.split(':')[0]));
        setSelectedProviders(new Set(failedIds));
      } else {
        setSelectedProviders(new Set());
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network unavailable';
      setBulkDeleteError(errorMessage);
      setTimeout(() => setBulkDeleteError(''), 5000);
    }

    setBulkDeleteDialogOpen(false);
  };

  if (error) {
    return (
      <div data-testid="llm-settings-container">
        <div data-testid="error-message">Error: {error}</div>
        <Button data-testid="retry-button" onClick={handleRetry}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="llm-settings-container">
      {/* Error Messages */}
      {bulkDeleteError && (
        <div data-testid="llm-status-message">
          <Alert intent="error">{bulkDeleteError}</Alert>
        </div>
      )}

      <DataGridCard
        items={filteredProviders}
        columns={columns}
        getRowId={(provider) => provider.id}
        toolbar={
          <DataGridToolbar>
            <DataGridToolbar.Left>
              <ToolbarButton
                icon={<ArrowClockwise20Regular />}
                onClick={() => refetch()}
                loading={isFetching}
                loadingText="Refreshing..."
              >
                Refresh
              </ToolbarButton>
            </DataGridToolbar.Left>
            <DataGridToolbar.Right>
              <ToolbarSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by name or type..."
              />
              {providers.length > 0 && (
                <ToolbarButton
                  icon={<Delete20Regular />}
                  onClick={handleBulkDelete}
                  disabled={selectedProviders.size === 0}
                >
                  {selectedProviders.size > 0 ? `Delete (${selectedProviders.size})` : 'Delete'}
                </ToolbarButton>
              )}
              <ToolbarButton appearance="primary" icon={<Add20Regular />} onClick={handleAddProvider}>
                Add Provider
              </ToolbarButton>
            </DataGridToolbar.Right>
          </DataGridToolbar>
        }
        pagination={{
          showing: filteredProviders.length,
          total: providers.length,
        }}
        selectable
        selectedIds={selectedProviders}
        onSelectionChange={handleSelectionChange}
        loading={isLoading}
        emptyMessage="No providers configured. Click 'Add Provider' to configure your first LLM provider."
        testId="llm-provider-table"
      />

      {/* Modals */}
      <LLMProviderModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveProvider}
        mode={modalMode}
        existingProvider={editingProvider || undefined}
        existingConfig={editingConfig || undefined}
        isDuplicating={isDuplicating}
      />

      <Dialog open={showDeleteConfirm}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Provider</DialogTitle>
            <DialogContent>Are you sure you want to delete this provider? This action cannot be undone.</DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingProviderId(null);
                }}
              >
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmDeleteProvider}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onOpenChange={(_event, data) => {
          if (!data.open) {
            setBulkDeleteDialogOpen(false);
          }
        }}
      >
        <DialogSurface data-testid="bulk-delete-dialog">
          <DialogBody>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete {selectedProviders.size} providers? This action cannot be undone.
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Text weight="semibold">Providers to be deleted:</Text>
                <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                  {Array.from(selectedProviders).map((providerId) => {
                    const provider = providers.find((p) => p.id === providerId);
                    if (provider && !provider.isActive) {
                      return (
                        <li key={providerId} style={{ marginBottom: '4px' }}>
                          {provider.displayName}
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
                {Array.from(selectedProviders).some((id) => {
                  const provider = providers.find((p) => p.id === id);
                  return provider && provider.isActive;
                }) && (
                  <div style={{ marginTop: '12px' }}>
                    <Text size={300} style={{ fontStyle: 'italic' }}>
                      Note: Active providers cannot be deleted and will be skipped.
                    </Text>
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" data-testid="bulk-delete-cancel-button" onClick={() => setBulkDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" data-testid="bulk-delete-confirm-button" onClick={handleConfirmBulkDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default LLMSettingsTab;
